/*
 * Cart cross-sell recommendations
 * ---------------------------------------------------------------------------
 * Turns the theme's two cart cross-sells from fixed lists into lists that answer
 * the cart.
 *
 * Both of them render a merchant-picked collection on the server and drop
 * whatever is already in the cart, which means adding one of their products only
 * ever *removed* that card: the row slid up by one and the next product from the
 * collection appeared at the bottom. Nothing about either responded to what the
 * customer had just chosen, which is the one thing a cross-sell beside a cart is
 * for.
 *
 * So the collection is the floor and this is the upgrade. Each block hands over
 * the cart it was rendered against and the name of a section; this asks
 * /recommendations/products what goes with those products, merges the answers and
 * writes them into the list the collection filled.
 *
 *   snippets/cart-addons.liquid          -> sections/cart-drawer-recommendations
 *   sections/cart-template.liquid        -> sections/cart-page-recommendations
 *
 * The two surfaces render different cards -- the drawer a compact one of its own,
 * the cart page the theme's standard product-item -- so each names its own
 * section, its own list and its own card selector, and nothing here knows which
 * is which. Everything it needs is on the block:
 *
 *   data-cart-recommendations                  the block itself
 *   -section        the section to render through the endpoint
 *   -list           selector for the list to write into, inside the block
 *   -card           selector for one card, in that list and in the response
 *   -intent         related | complementary
 *   -limit          how many cards the row holds
 *   -cart-products  the cart's product ids, newest first
 *   -gap            cents short of free shipping, or 0 (optional)
 *   -eager          refresh on load rather than waiting to be opened
 *
 * A block missing them is a merchant who turned the matching off, and is left
 * exactly as the server rendered it.
 *
 * Liquid has no `recommendations` object outside the /recommendations/products
 * route -- it is populated for a section rendered through that route and nowhere
 * else -- which is why neither surface can resolve this in its own render and
 * both need a section of their own.
 */
(function () {
  'use strict';

  var BLOCK = '[data-cart-recommendations]';
  var CART_TOGGLE = '[data-action="toggle-mini-cart"]';

  var ANCHOR_LIMIT = 3; // cart products asked about; one request each
  var FETCH_LIMIT = 10; // per anchor, and the endpoint's own ceiling

  var recommendationCache = {};
  var lastCartProductIds = [];
  var refreshGeneration = 0;

  function parseIds(value) {
    var seen = {};

    return (value || '').split(',').reduce(function (ids, raw) {
      var id = raw.replace(/\s/g, '');

      if (id && !seen[id]) {
        seen[id] = true;
        ids.push(id);
      }

      return ids;
    }, []);
  }

  // A product that leaves the cart has been filtered out of every cached answer
  // that was rendered while it was still there, so those answers can never offer
  // it again. Cheaper to drop the cache than to reason about which entries are
  // affected, and it only happens on a removal.
  function invalidateCacheOnRemoval(cartProductIds) {
    var present = {};

    cartProductIds.forEach(function (id) {
      present[id] = true;
    });

    var removed = lastCartProductIds.some(function (id) {
      return !present[id];
    });

    if (removed) {
      recommendationCache = {};
    }

    lastCartProductIds = cartProductIds;
  }

  // Reads the cards out of a rendered section. Shopify wraps the response in its
  // own #shopify-section element, and the cards are all that is wanted.
  function cardsFrom(html, cardSelector) {
    var holder = document.createElement('div');
    holder.innerHTML = html || '';

    return Array.prototype.map
      .call(holder.querySelectorAll(cardSelector), function (card) {
        return {
          id: card.getAttribute('data-product-id'),
          price: parseInt(card.getAttribute('data-product-price'), 10),
          html: card.outerHTML
        };
      })
      .filter(function (card) {
        return !!card.id;
      });
  }

  function fetchRecommendations(productId, intent, sectionId, cardSelector) {
    // Keyed by section as well as product because the two surfaces render the
    // same recommendation as two different cards.
    var key = [productId, intent, sectionId].join('|');

    if (recommendationCache[key]) {
      return recommendationCache[key];
    }

    var base =
      (window.routes && window.routes.productRecommendationsUrl) || '/recommendations/products';

    var url =
      base +
      (base.indexOf('?') === -1 ? '?' : '&') +
      'section_id=' +
      encodeURIComponent(sectionId) +
      '&product_id=' +
      encodeURIComponent(productId) +
      '&intent=' +
      encodeURIComponent(intent) +
      '&limit=' +
      FETCH_LIMIT;

    var request = fetch(url, { credentials: 'same-origin', method: 'GET' })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('recommendations unavailable');
        }

        return response.text();
      })
      .then(function (html) {
        return cardsFrom(html, cardSelector);
      })
      .catch(function () {
        // Neither a dropped request nor a 500 is an answer, so neither is kept:
        // the endpoint may well have one on the next redraw, and the collection
        // covers the row until it does. An empty 200 is an answer and is cached.
        // Compared by identity because the cache may have been dropped and
        // refilled while this request was still out.
        if (recommendationCache[key] === request) {
          delete recommendationCache[key];
        }

        return [];
      });

    recommendationCache[key] = request;

    return request;
  }

  // One card from each anchor in turn, rather than one anchor's whole list before
  // the next. Recommendations for two cart products overlap heavily, so a plain
  // concatenation would spend most of the row on the first product.
  function mergeRecommendations(lists, excludeIds, limit) {
    var excluded = {};
    var taken = {};
    var merged = [];
    var depth = 0;
    var deepest = 0;

    excludeIds.forEach(function (id) {
      excluded[id] = true;
    });

    lists.forEach(function (list) {
      deepest = Math.max(deepest, list.length);
    });

    for (; depth < deepest && merged.length < limit; depth++) {
      for (var i = 0; i < lists.length && merged.length < limit; i++) {
        var card = lists[i][depth];

        if (!card || taken[card.id] || excluded[card.id]) {
          continue;
        }

        taken[card.id] = true;
        merged.push(card);
      }
    }

    return { cards: merged, taken: taken };
  }

  // Under the free shipping line, the reward ladder above the row is asking for
  // an exact amount; the row leads with the products that cover it on their own,
  // cheapest first, and keeps the ranking for the rest. The server render deals
  // the collection the same way (snippets/cart-addon-cards.liquid), so the swap
  // to recommendations never demotes a closer. Cards without a price -- an
  // older render, a card the section did not stamp -- are never promoted.
  function leadWithGapClosers(cards, gap) {
    if (!gap || gap <= 0) {
      return cards;
    }

    var closers = [];
    var rest = [];

    cards.forEach(function (card) {
      if (!isNaN(card.price) && card.price >= gap) {
        closers.push(card);
      } else {
        rest.push(card);
      }
    });

    // Indexed so the sort stays stable where prices tie: two closers at one
    // price keep the order the ranking gave them.
    closers = closers
      .map(function (card, index) {
        return { card: card, index: index };
      })
      .sort(function (a, b) {
        return a.card.price - b.card.price || a.index - b.index;
      })
      .map(function (entry) {
        return entry.card;
      });

    return closers.concat(rest);
  }

  function idsOf(cards) {
    return cards
      .map(function (card) {
        return card.id;
      })
      .join(',');
  }

  // Everything needed to fetch and write one block, or null when the block is
  // not asking to be matched to the cart.
  function readBlock(element) {
    var plan = {
      element: element,
      sectionId: element.getAttribute('data-cart-recommendations-section'),
      listSelector: element.getAttribute('data-cart-recommendations-list'),
      cardSelector: element.getAttribute('data-cart-recommendations-card'),
      intent: element.getAttribute('data-cart-recommendations-intent'),
      limit: parseInt(element.getAttribute('data-cart-recommendations-limit'), 10) || 6,
      cartProductIds: parseIds(element.getAttribute('data-cart-recommendations-cart-products')),
      gap: parseInt(element.getAttribute('data-cart-recommendations-gap'), 10) || 0
    };

    if (!plan.sectionId || !plan.listSelector || !plan.cardSelector || !plan.intent) {
      return null;
    }

    if (!plan.cartProductIds.length) {
      return null;
    }

    plan.list = element.querySelector(plan.listSelector);

    return plan.list ? plan : null;
  }

  function applyRecommendations(plan, lists) {
    var merged = mergeRecommendations(lists, plan.cartProductIds, plan.limit);

    // Nothing came back -- a store with no recommendation data for these products
    // yet, or an endpoint that failed. The collection is the floor and stands.
    if (!merged.cards.length) {
      return;
    }

    // Short of a full row, the cards already on screen fill the rest, so a thin
    // recommendation set never shrinks the list.
    var onScreen = Array.prototype.map.call(plan.list.children, function (card) {
      return {
        id: card.getAttribute('data-product-id'),
        price: parseInt(card.getAttribute('data-product-price'), 10),
        html: card.outerHTML
      };
    });

    onScreen.forEach(function (card) {
      if (merged.cards.length < plan.limit && card.id && !merged.taken[card.id]) {
        merged.taken[card.id] = true;
        merged.cards.push(card);
      }
    });

    merged.cards = leadWithGapClosers(merged.cards, plan.gap);

    plan.element.setAttribute('data-cart-recommendations-source', 'recommendations');

    // Both surfaces redraw on every cart change, and most redraws resolve to the
    // row already rendered. Writing it again would flicker, and on the drawer it
    // would throw away the strip's scroll position too.
    if (idsOf(merged.cards) === idsOf(onScreen)) {
      return;
    }

    plan.list.innerHTML = merged.cards
      .map(function (card) {
        return card.html;
      })
      .join('');

    // A different row: any offset belongs to the old one. cart-drawer.js keeps
    // the strip's own bookkeeping and follows the scroll event this fires.
    plan.list.scrollLeft = 0;
  }

  function refresh(eagerOnly) {
    var blocks = document.querySelectorAll(BLOCK);

    if (!blocks.length) {
      return;
    }

    // Bumped once per pass rather than per block: a pass describes one cart, and
    // an answer that arrives after the next pass has started describes an older
    // one whichever block asked for it.
    var generation = ++refreshGeneration;
    var invalidated = false;

    Array.prototype.forEach.call(blocks, function (element) {
      if (eagerOnly && !element.hasAttribute('data-cart-recommendations-eager')) {
        return;
      }

      var plan = readBlock(element);

      if (!plan) {
        return;
      }

      if (!invalidated) {
        invalidateCacheOnRemoval(plan.cartProductIds);
        invalidated = true;
      }

      Promise.all(
        plan.cartProductIds.slice(0, ANCHOR_LIMIT).map(function (productId) {
          return fetchRecommendations(productId, plan.intent, plan.sectionId, plan.cardSelector);
        })
      ).then(function (lists) {
        // A cached pass started after this one can finish first. Whichever was
        // asked for last describes the cart as it is now, so an older answer is
        // dropped rather than written over it.
        if (generation !== refreshGeneration) {
          return;
        }

        applyRecommendations(plan, lists);
      });
    });
  }

  // theme.js dispatches this after redrawing the drawer and after redrawing the
  // cart section, which is every cart change on either surface. It is dispatched
  // on the header section and does not bubble on the drawer, so the listener is
  // on the document in the capture phase.
  document.addEventListener(
    'cart:rerendered',
    function () {
      refresh(false);
    },
    true
  );

  // A block that is already on screen -- the cart page's -- has to be right
  // before anything happens. The drawer's is behind a closed panel, so a customer
  // who never opens it never pays for the requests; opening it is the other
  // moment its row has to be right, and by then the fetches are usually cached.
  document.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest(CART_TOGGLE)) {
      refresh(false);
    }
  });

  function refreshEager() {
    refresh(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshEager);
  } else {
    refreshEager();
  }
})();
